import { env } from "@/config/env";

/**
 * Hosts de bucket que aparecem em registros antigos, quando a URL absoluta do
 * storage foi gravada no banco em vez da key. São reescritos para o CDN público
 * correspondente — que é o único host liberado no next.config.
 */
const RAW_STORAGE_HOST = /(\.amazonaws\.com|\.r2\.cloudflarestorage\.com)$/i;

/**
 * Resolve um caminho de mídia da API contra um host público. Regras:
 *
 *  - caminho relativo ("media/iapo/x.png" ou "/media/…") → host + path
 *  - URL absoluta de bucket cru (…amazonaws.com, …r2.cloudflarestorage.com) →
 *    reescreve o host preservando o path
 *  - qualquer outra URL absoluta (já num CDN/host público, ou no host do
 *    fornecedor no caso de produtos) → intacta
 *
 * Sem caminho, retorna null — o MediaImage não renderiza nada.
 */
function resolveAgainst(host: string, path: string | null | undefined): string | null {
    if (!path) return null;

    // Relativo → prefixa com o host.
    if (!/^https?:\/\//.test(path)) {
        return `${host}/${path.replace(/^\/+/, "")}`;
    }

    // Absoluto apontando direto para o bucket → normaliza para o host público.
    try {
        const url = new URL(path);
        if (RAW_STORAGE_HOST.test(url.hostname)) {
            return `${host}${url.pathname}${url.search}`;
        }
    } catch {
        return path;
    }

    // Já é uma URL absoluta pública → mantém.
    return path;
}

/**
 * URL efêmera criada pelo editor do portal (`blob:`/`data:`) — só existe na
 * aba que a gerou. Chega gravada em `app_visual_identity.theme.*.graphics.logo`
 * quando o cliente salva a identidade sem concluir o upload; na loja não
 * resolve nada (e, como não casa com `^https?://`, seria tratada como caminho
 * relativo e viraria um GET no host de mídia). Quem lê o logo deve descartá-la
 * e cair no `app_assets`, que é a mídia de verdade.
 */
export function isEphemeralMediaUrl(path: string | null | undefined): boolean {
    return !!path && /^(blob:|data:)/i.test(path.trim());
}

/**
 * Mídia do portal: uploads, assets, banners, logos, imagens de categoria.
 * Resolvida contra `env.mediaUrl`.
 */
export function resolveMediaUrl(path: string | null | undefined): string | null {
    return resolveAgainst(env.mediaUrl, path);
}

/**
 * Imagens de produto: bucket/CDN próprio (`env.productsMediaUrl`), separado da
 * mídia do portal. Use nos componentes de catálogo, carrinho, checkout e
 * pedidos — em qualquer lugar cujo caminho venha de `product.image`/`images[]`.
 *
 * Imagens que ainda chegam como URL absoluta do fornecedor passam intactas,
 * como antes.
 */
export function resolveProductImageUrl(path: string | null | undefined): string | null {
    return resolveAgainst(env.productsMediaUrl, path);
}
